import React, { useState, useEffect, useCallback } from 'react';
import { useClient } from '../contexts/ClientContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useTaskCost } from '../hooks/useTaskCost';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/config';
import { generateCompleteARUrl } from '../utils/arUrlGenerator';
import mediaService, { MediaFile } from '../services/mediaService';
import AuthenticatedImage from '../components/media/AuthenticatedImage';
import { MultiMediaFileSelector } from '../components/media/MultiMediaFileSelector';
import ComingSoonWrapper from '../components/launch/ComingSoonWrapper';
import {
  Box,
  Wand2,
  Download,
  Loader2,
  RefreshCw,
  Smartphone,
  Eye,
  QrCode,
  Share2,
  AlertCircle,
  ImageIcon,
  HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import QRCodeGenerator from '../components/ar/QRCode';
import { motion, AnimatePresence } from 'framer-motion';
import { useTour } from '../contexts/TourContext';
import { adStudioARSteps } from '../components/onboarding/tour-steps';
import { useMobile } from '../hooks/useMobile';


const AdStudioAR2Page: React.FC = () => {
  const { currentClient } = useClient();
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { startTour } = useTour();
  const isMobile = useMobile();

  // Task costs (credit-based only)
  const arAdTask = useTaskCost('3D Model to AR Ad Generation');

  // State for 3D model selection
  const [selected3DModels, setSelected3DModels] = useState<MediaFile[]>([]);
  const [thumbnailImageId, setThumbnailImageId] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  // State for fallback source image (when no thumbnail found)
  const [selectedSourceImage, setSelectedSourceImage] = useState<MediaFile[]>([]);
  const [showImageSelector, setShowImageSelector] = useState(false);

  // AR ad generation state
  const [isGeneratingArAd, setIsGeneratingArAd] = useState(false);
  const [generatedArAdUrl, setGeneratedArAdUrl] = useState<string | null>(null);
  const [arViewerUrl, setArViewerUrl] = useState<string | null>(null);
  const [showArQrCode, setShowArQrCode] = useState(false);
  const [arAdGenerationFailed, setArAdGenerationFailed] = useState(false);
  const [arAdPollingInterval, setArAdPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Filter function for 3D models
  const filter3DModels = useCallback((files: MediaFile[]) => {
    return files.filter(file =>
      file.mime_type === 'model/gltf-binary' ||
      file.original_filename?.toLowerCase().endsWith('.glb')
    );
  }, []);

  // Handle pre-selected model from navigation state
  useEffect(() => {
    const navigationState = location.state as {
      preSelectedModel?: string;
      preSelectedSourceImage?: string;
      sourceImageUrl?: string;
    } | null;

    if (navigationState?.preSelectedModel && currentClient) {
      console.log('🎯 Pre-selected model from navigation:', navigationState.preSelectedModel);

      // Fetch the pre-selected 3D model
      const fetchPreSelectedModel = async () => {
        try {
          const mediaFile = await mediaService.getMediaFile(navigationState.preSelectedModel, currentClient.id);
          
          if (mediaFile) {
            setSelected3DModels([mediaFile]);

            // Set the pre-selected source image as thumbnail
            if (navigationState.preSelectedSourceImage && navigationState.sourceImageUrl) {
              setThumbnailImageId(navigationState.preSelectedSourceImage);
              setThumbnailUrl(navigationState.sourceImageUrl);
              setShowImageSelector(false);

              toast({
                title: "🎯 3D Model Loaded",
                description: "Your 3D model and source image are ready! You can now generate your AR visuals.",
              });
            }
          }

          // Clear the navigation state after using it to prevent persistence on refresh
          navigate(location.pathname, { replace: true, state: null });
        } catch (error) {
          console.error('Error fetching pre-selected model:', error);
          toast({
            title: "Error",
            description: "Failed to load the pre-selected 3D model",
            variant: "destructive",
          });
        }
      };

      fetchPreSelectedModel();
    }
  }, [location.state, currentClient, toast, navigate, location.pathname]);

  // Handle 3D model selection and find thumbnail
  const handle3DModelSelection = useCallback(async (models: MediaFile[]) => {
    // Filter to only 3D models
    const filteredModels = filter3DModels(models);
    setSelected3DModels(filteredModels);
    setThumbnailImageId(null);
    setThumbnailUrl(null);
    setShowImageSelector(false);
    setSelectedSourceImage([]);

    if (filteredModels.length === 0) {
      return;
    }

    const selectedModel = filteredModels[0]; // Use first selected model
    console.log('🎯 Selected 3D model:', selectedModel.original_filename);

    // Try to find the thumbnail image for this 3D model
    try {
      const taskIdMatch = selectedModel.original_filename.match(/meshy_3d_(.+)\.glb$/);
      if (taskIdMatch) {
        const taskId = taskIdMatch[1];
        console.log('🎯 Extracted task ID:', taskId);

        // Search for the thumbnail file with the same task ID
        // Use mediaService to get media files (handles API calls correctly)
        const mediaResponse = await mediaService.getMediaFiles(1, 100, undefined, currentClient?.id);
        console.log('🔍 All media files:', mediaResponse.files?.length || 0);
        
        const thumbnailFile = (mediaResponse.files || []).find((file: MediaFile) => {
            const hasTag = file.tags?.includes('3d-thumbnail');
            const hasTaskId = file.original_filename.includes(taskId);
            const isImage = file.mime_type.startsWith('image');
            
            console.log('🔍 Checking file:', {
              filename: file.original_filename,
              tags: file.tags,
              mime_type: file.mime_type,
              hasTag,
              hasTaskId,
              isImage,
              matches: hasTag && hasTaskId && isImage
            });
            
            return hasTag && hasTaskId && isImage;
          });

          if (thumbnailFile) {
            console.log('🎯 Found thumbnail file:', thumbnailFile.id);
            setThumbnailImageId(thumbnailFile.id);
            setThumbnailUrl(thumbnailFile.thumbnail_url || thumbnailFile.file_url);
            toast({
              title: "✅ Ready for AR Generation",
              description: "Found the original image used to create this 3D model. You can now generate your AR showcase!",
            });
          } else {
            console.log('🎯 No thumbnail found for task ID:', taskId);
            setShowImageSelector(true);
            toast({
              title: "Source Image Needed",
              description: "This 3D model doesn't have an associated image. Please select a source image to continue with AR ad generation.",
              variant: "default",
            });
          }
      } else {
        console.log('🎯 Could not extract task ID from filename:', selectedModel.original_filename);
        setShowImageSelector(true);
        toast({
          title: "Source Image Required",
          description: "We couldn't find the original image for this 3D model. Please select a source image to create your AR showcase.",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error finding thumbnail:', error);
      setShowImageSelector(true);
      toast({
        title: "Source Image Needed",
        description: "Unable to locate the original image for this 3D model. Please select a source image to proceed.",
        variant: "default",
      });
    }
  }, [currentClient, toast]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (arAdPollingInterval) {
        clearInterval(arAdPollingInterval);
      }
    };
  }, [arAdPollingInterval]);

  // AR ad polling function (reused from AdStudioAR.tsx)
  const pollArAdTaskStatus = (taskId: string) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'X-Client-ID': currentClient?.id || '',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch task status');
        }

        const task = await response.json();

        if (task.status.toUpperCase() === 'COMPLETED') {
          clearInterval(interval);
          setArAdPollingInterval(null);
          if (task.output_media_file_ids && task.output_media_file_ids.length > 0 && currentClient) {
            const mediaFile = await mediaService.getMediaFile(task.output_media_file_ids[0], currentClient.id);
            setGeneratedArAdUrl(mediaFile.file_url || mediaFile.thumbnail_url);
          }
          setIsGeneratingArAd(false);

          // Refresh user data and credits
          try {
            await refreshUser();
            queryClient.invalidateQueries({ queryKey: ['feature-limit'] });
          } catch (error) {
            console.warn('Failed to refresh user data:', error);
          }

          toast({
            title: "AR Ad Generated",
            description: "Your AR ad creative is ready.",
          });
        } else if (task.status === 'FAILED') {
          clearInterval(interval);
          setArAdPollingInterval(null);
          setIsGeneratingArAd(false);
          setArAdGenerationFailed(true);
          toast({
            title: "AR Ad Generation Failed",
            description: task.error_message || "An error occurred during ad generation.",
            variant: "destructive",
          });
        }
      } catch (error) {
        clearInterval(interval);
        setArAdPollingInterval(null);
        setIsGeneratingArAd(false);
        console.error('Error polling task status:', error);
        toast({
          title: "Error",
          description: "Failed to get task status.",
          variant: "destructive",
        });
      }
    }, 3000);

    setArAdPollingInterval(interval);
  };

  // Reset AR ad results when selection changes
  useEffect(() => {
    setGeneratedArAdUrl(null);
    setArViewerUrl(null);
    setArAdGenerationFailed(false);
  }, [selected3DModels, thumbnailImageId, selectedSourceImage]);

  // Handle AR ad creation (reused logic from AdStudioAR.tsx)
  const handleCreateARAd = async () => {
    if (selected3DModels.length === 0 || !currentClient) {
      toast({
        title: "Error",
        description: "Please select a 3D model first.",
        variant: "destructive",
      });
      return;
    }

    // Determine source image
    let sourceImageId: string;
    let sourceImageUrl: string;

    if (thumbnailImageId && thumbnailUrl) {
      // Use the found thumbnail image
      sourceImageId = thumbnailImageId;
      sourceImageUrl = thumbnailUrl;
      console.log('🎯 Using thumbnail image:', sourceImageId);
    } else if (selectedSourceImage.length > 0) {
      // Use user-selected source image
      sourceImageId = selectedSourceImage[0].id;
      sourceImageUrl = selectedSourceImage[0].thumbnail_url || selectedSourceImage[0].file_url;
      console.log('🎯 Using user-selected image:', sourceImageId);
    } else {
      toast({
        title: "Source Image Required",
        description: "A source image is needed to generate your AR content. Please select one to continue.",
        variant: "default",
      });
      return;
    }

    const selectedModel = selected3DModels[0];

    setIsGeneratingArAd(true);
    setArAdGenerationFailed(false);

    // Generate AR viewer URL using centralized utility
    const useMockForAR = import.meta.env.VITE_USE_MOCK_3D === 'true';
    const viewerUrl = generateCompleteARUrl(
      selectedModel.id,
      sourceImageUrl,
      useMockForAR
    );

    console.log('🎯 Generated AR Viewer URL:', viewerUrl);
    console.log('🎯 Using source image ID:', sourceImageId);
    console.log('🎯 Using source image URL:', sourceImageUrl);
    setArViewerUrl(viewerUrl);

    try {
      // Step 1: Generate the enhanced prompt
      const promptGenerationEndpoint = `${getApiUrl()}/api/v1/ai/generate-ar-ad-prompt`;
      const basePrompt = "Create a realistic AR visual showing a hand holding a smartphone displaying the product in its natural environment. Analyze the product type and place it contextually - kitchen items on counters, bags on tables/dressers, decor on appropriate surfaces, electronics on desks/entertainment centers. The background shows the environment without the product, while the phone screen shows the same space with the product naturally placed. Professional AR demonstration with realistic lighting and proper scale.";

      const promptGenResponse = await fetch(promptGenerationEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify({
          input_media_file_id: sourceImageId,
          base_prompt: basePrompt,
        }),
      });

      if (!promptGenResponse.ok) {
        throw new Error('Failed to generate enhanced prompt');
      }

      const promptGenResult = await promptGenResponse.json();
      const enhancedPrompt = promptGenResult.enhanced_prompt;

      // Step 2: Generate the ad creative with the enhanced prompt
      const endpoint = `${getApiUrl()}/api/v1/ai/enhance-product`;
      const payload = {
        input_media_file_id: sourceImageId,
        description: enhancedPrompt,
        ad_intent: 'product-showcase',
        format_composition: 'with-person-model',
        style_art_direction: 'tech-futuristic',
        skip_prompt_generation: true,
        ar_viewer_url: viewerUrl
      };

      console.log("AR Ad payload:", JSON.stringify(payload, null, 2));
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create AI task');
      }

      const result = await response.json();

      // Refresh user data and credits immediately after task creation
      try {
        await refreshUser();
        queryClient.invalidateQueries({ queryKey: ['feature-limit'] });
      } catch (error) {
        console.warn('Failed to refresh user data:', error);
      }

      if (result.task && result.task.id) {
        pollArAdTaskStatus(result.task.id);
      }

      toast({
        title: "AR Ad Generation Started",
        description: "Your AR ad is being generated and will appear shortly.",
      });

    } catch (error: any) {
      console.error('Error creating AR ad task:', error);
      toast({
        title: "Error",
        description: `Failed to start AR ad generation: ${error.message}`,
        variant: "destructive",
      });
      setIsGeneratingArAd(false);
      setArAdGenerationFailed(true);
    }
  };

  // Download AR ad
  const handleDownloadArAd = async () => {
    if (!generatedArAdUrl) return;

    try {
      const response = await fetch(generatedArAdUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ar-ad-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Download Started",
        description: "Your AR ad image is being downloaded.",
      });
    } catch (error) {
      console.error('Failed to download AR ad:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download the AR ad image. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <ComingSoonWrapper
      feature="ar_generation"
      title="AR Studio Coming Soon"
      description="We are working hard to get AR Studio ready. You will be able to transform your 3D models into immersive augmented reality experiences."
      features={[
        "Convert 3D models to AR-ready formats",
        "Generate QR codes for instant AR viewing",
        "Mobile-optimized AR experiences"
      ]}
      icon="zap"
    >
      <div className="min-h-screen bg-background text-foreground">
        {/* Header */}
        <div className="backdrop-blur-2xl bg-background/60 border-b border-border/30">
          <div className="px-4 sm:px-6 py-3">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                    <Smartphone className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse"></div>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-500 via-teal-600 to-cyan-600 bg-clip-text text-transparent">
                      AR Studio
                    </h1>
                    <p className="hidden sm:block text-muted-foreground">Create AR content from your 3D models</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                {/* Tour Button */}
                {!isMobile && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    startTour(adStudioARSteps);
                  }}
                  className="px-3 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 hover:from-emerald-100 hover:to-teal-100 dark:hover:from-emerald-900/30 dark:hover:to-teal-900/30 text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200 transition-all duration-200 rounded text-sm flex items-center gap-2"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span className="hidden sm:inline font-medium">Tour</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 py-6 sm:py-8">
        {/* Pre-selection notification */}
        {location.state?.preSelectedModel && (
          <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                <Box className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-800 dark:text-emerald-200">3D Model Pre-Selected</h3>
                <p className="text-sm text-emerald-600 dark:text-emerald-300">
                  Your 3D model and source image are ready. Click "Generate AR" to create your content!
                </p>
              </div>
            </div>
          </div>
        )}

        <TooltipProvider>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 lg:gap-8">

            {/* Left Panel - Form */}
            <div className="xl:col-span-4 space-y-6">
              <Card data-tour="3d-model-selector" className={showImageSelector ? "border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50/30 to-teal-50/30 dark:from-emerald-900/20 dark:to-teal-900/20" : ""}>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                      <Box className="w-4 h-4 text-white" />
                    </div>
                    Select 3D Model
                    {showImageSelector && (
                      <div className="ml-auto">
                        <div className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/40 rounded-full text-xs font-medium text-emerald-700 dark:text-emerald-300">
                          Step 1 of 2
                        </div>
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <MultiMediaFileSelector
                    selectedFiles={selected3DModels}
                    onSelectionChange={handle3DModelSelection}
                    maxFiles={1}
                    acceptedTypes={['other']}
                    customFilter={filter3DModels}
                    className="min-h-[300px]"
                    placeholder="Select a 3D model to create AR visuals"
                    theme="green"
                  />

                  {/* Show selected 3D model */}
                  {selected3DModels.length > 0 && (
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 rounded-lg flex items-center justify-center">
                          {thumbnailImageId ? (
                            <AuthenticatedImage
                              src={`/api/v1/media/${selected3DModels[0].id}/thumbnail`}
                              alt={selected3DModels[0].original_filename}
                              className="w-full h-full object-cover rounded-lg"
                              clientId={currentClient?.id}
                            />
                          ) : (
                            <Box className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{selected3DModels[0].original_filename}</span>
                          </div>
                          {thumbnailImageId ? (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              ✓ {location.state?.preSelectedModel ? 'Pre-selected from 3D Studio' : 'Original image found'} - ready to generate AR ad
                            </p>
                          ) : showImageSelector ? (
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                              📷 Source image required for AR generation
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground mt-1">
                              🔍 Searching for original image...
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Visual connector between steps */}
              {showImageSelector && (
                <div className="flex items-center justify-center py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-px bg-gradient-to-r from-emerald-300 to-transparent dark:from-emerald-600"></div>
                    <div className="text-xs font-medium px-3 py-1.5 bg-gradient-to-r from-emerald-100 to-slate-100 dark:from-emerald-900/30 dark:to-slate-800/30 rounded-full text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                      THEN
                    </div>
                    <div className="w-12 h-px bg-gradient-to-l from-slate-300 to-transparent dark:from-slate-600"></div>
                  </div>
                </div>
              )}

              {/* Source Image Selection (only if no thumbnail) */}
              {showImageSelector && (
                <Card data-tour="source-image-selector" className="border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50/30 to-gray-50/30 dark:from-slate-800/20 dark:to-gray-800/20">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <div className="w-6 h-6 bg-gradient-to-br from-slate-500 to-gray-600 rounded-lg flex items-center justify-center">
                        <ImageIcon className="w-4 h-4 text-white" />
                      </div>
                      Select Source Image
                      <div className="ml-auto">
                        <div className="px-2 py-1 bg-slate-100 dark:bg-slate-800/60 rounded-full text-xs font-medium text-slate-600 dark:text-slate-400">
                          Step 2 of 2
                        </div>
                      </div>
                    </CardTitle>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      📷 Choose an image that will be used as the basis for your AR content
                    </p>
                  </CardHeader>
                  <CardContent>
                    <MultiMediaFileSelector
                      selectedFiles={selectedSourceImage}
                      onSelectionChange={setSelectedSourceImage}
                      maxFiles={1}
                      acceptedTypes={['image']}
                      className="min-h-[200px]"
                      placeholder="Select an image to use for AR ad generation"
                    />
                  </CardContent>
                </Card>
              )}

              {/* Generate Button */}
              <div className="relative pt-3 border-t border-border/50" data-tour="generate-ar-button">
                    <Button
                      onClick={handleCreateARAd}
                      disabled={
                        isGeneratingArAd ||
                        arAdTask.isLimitReached ||
                        selected3DModels.length === 0 ||
                        (!thumbnailImageId && selectedSourceImage.length === 0)
                      }
                      className={`w-full h-10 font-semibold text-sm rounded-lg shadow-lg transition-all duration-300 border-0 ${arAdTask.isLimitReached
                        ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60'
                        : 'bg-gradient-to-r from-emerald-500 via-teal-600 to-cyan-600 hover:from-emerald-600 hover:via-teal-700 hover:to-cyan-700 text-white shadow-emerald-500/25 hover:shadow-emerald-500/40 transform hover:scale-105 disabled:opacity-50 disabled:transform-none disabled:cursor-not-allowed'
                        }`}
                    >
                      {isGeneratingArAd ? (
                        <div className="flex items-center space-x-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Creating...</span>
                        </div>
                      ) : arAdTask.isLimitReached ? (
                        <div className="flex items-center space-x-2">
                          <Wand2 className="w-4 h-4" />
                          <span>Limit Reached</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between w-full px-1">
                          <div className="flex items-center space-x-1">
                            <Wand2 className="w-3 h-3" />
                            <span className="text-base font-semibold">Generate AR Ad</span>
                          </div>
                          <div className="flex items-center space-x-1 bg-white/20 px-1.5 py-0.5 rounded-full">
                            <span className="text-xs font-bold">{arAdTask.cost}</span>
                          </div>
                        </div>
                      )}
                    </Button>
              </div>

              {/* Usage Guide Section */}
              {arViewerUrl && (
                <div className="p-4 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-xl border border-blue-200/30 dark:border-blue-700/30">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Smartphone className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-card-foreground text-sm">How to Use Your AR Ad</h4>
                      <p className="text-xs text-muted-foreground mt-1">Share your AR experience</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start gap-2 p-2 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                      <QrCode className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-card-foreground">QR Code</div>
                        <div className="text-muted-foreground">Print on materials or display in-store</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2 p-2 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                      <Share2 className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-card-foreground">AR Link</div>
                        <div className="text-muted-foreground">Share via social media or messaging</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2 p-2 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg border border-emerald-200/50 dark:border-emerald-700/50">
                    <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300">
                      <Eye className="w-3 h-3" />
                      <span className="font-medium">No app required!</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Panel - Canvas */}
            <div className="xl:col-span-8">
              <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border border-gray-200 shadow-xl shadow-slate-500/5 min-h-[450px] lg:min-h-[650px]" data-tour="ar-preview-canvas">
                {/* Header */}
                <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-r from-card/95 to-card/80 backdrop-blur-xl border-b border-border/30 p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                        <Smartphone className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-card-foreground">AR Canvas</h3>
                    </div>
                    {generatedArAdUrl && (
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadArAd()} className="text-muted-foreground text-xs sm:text-sm">
                          <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          Download
                        </Button>

                      </div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="absolute inset-0 pt-16 sm:pt-20 p-4 sm:p-6">
                  {isGeneratingArAd ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center space-y-4 sm:space-y-6">
                        <div className="relative w-32 h-32 sm:w-40 sm:h-40 mx-auto">
                          <div className="absolute inset-0 flex items-center justify-center">
                            {/* Main logo with subtle scaling animation */}
<img 
                              src="/icon-light.png" 
                              alt="Quixocial" 
                              className="w-14 h-14 sm:w-16 sm:h-16 animate-pulse"
                            />
                            
                            {/* Rotating glow rings around the logo */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-20 h-20 sm:w-24 sm:h-24 border-2 border-blue-500/60 dark:border-blue-400/30 rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-24 h-24 sm:w-28 sm:h-28 border border-purple-500/50 dark:border-purple-400/20 rounded-full animate-spin" style={{ animationDuration: '4s', animationDirection: 'reverse' }}></div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-28 h-28 sm:w-32 sm:h-32 border border-pink-500/40 dark:border-pink-400/15 rounded-full animate-spin" style={{ animationDuration: '5s' }}></div>
                            </div>
                            
                            {/* Pulsing glow effect behind logo */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-lg animate-pulse"></div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 sm:space-y-3">
                          <h3 className="text-xl sm:text-2xl font-bold text-card-foreground">
                            Creating Your AR visual
                          </h3>
                          <p className="text-muted-foreground max-w-md text-sm sm:text-base">
                            Generating stunning AR content with AI magic
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : arAdGenerationFailed ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center space-y-4 sm:space-y-6">
                        <AlertCircle className="w-16 h-16 sm:w-20 sm:h-20 text-destructive mx-auto" />
                        <div className="space-y-2">
                          <h3 className="text-xl sm:text-2xl font-bold text-destructive">Generation Failed</h3>
                          <p className="text-muted-foreground text-sm sm:text-base">AR Ad generation encountered an error.</p>
                        </div>
                        <Button onClick={handleCreateARAd} variant="destructive" size="lg">
                          Try Again
                        </Button>
                      </div>
                    </div>
                  ) : generatedArAdUrl ? (
                    <div className="w-full h-full flex flex-col">
                      {/* Two Column Layout */}
                      <div className="flex-1 grid grid-cols-2 gap-6 p-6">
                        {/* Left Column - Image and URL */}
                        <div className="flex flex-col space-y-4">
                          {/* Image */}
                          <div className="flex-1 flex items-center justify-center">
                            <div className="relative overflow-hidden rounded-xl shadow-2xl">
                              <img
                                src={generatedArAdUrl}
                                alt="Generated AR content"
                                className="max-w-full max-h-[400px] object-contain"
                              />
                            </div>
                          </div>

                          {/* AR URL below image */}
                          {arViewerUrl && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Smartphone className="w-4 h-4" />
                                <span className="font-medium text-sm">AR Viewer URL</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={arViewerUrl}
                                  readOnly
                                  className="flex-1 bg-background text-sm px-3 py-2 rounded border"
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(arViewerUrl);
                                    toast({
                                      title: "Copied!",
                                      description: "AR viewer URL copied to clipboard",
                                    });
                                  }}
                                >
                                  Copy
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right Column - QR Code */}
                        <div className="flex flex-col items-center justify-center space-y-4">
                          {arViewerUrl && (
                            <>
                              <div className="text-center p-6 bg-white dark:bg-slate-800 rounded-lg border">
                                <QRCodeGenerator url={arViewerUrl} size={200} />
                                <p className="text-sm text-muted-foreground mt-4">
                                  Scan with your phone to view in AR
                                </p>
                              </div>
                              <Button
                                onClick={() => {
                                  // Download QR code functionality
                                  const canvas = document.createElement('canvas');
                                  const ctx = canvas.getContext('2d');
                                  if (ctx) {
                                    const qrElement = document.querySelector('svg[data-testid="qr-code"]') as SVGElement;
                                    if (qrElement) {
                                      const svgData = new XMLSerializer().serializeToString(qrElement);
                                      const img = new Image();
                                      img.onload = () => {
                                        canvas.width = img.width;
                                        canvas.height = img.height;
                                        ctx.drawImage(img, 0, 0);
                                        const link = document.createElement('a');
                                        link.download = 'ar-qr-code.png';
                                        link.href = canvas.toDataURL();
                                        link.click();
                                      };
                                      img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
                                    }
                                  }
                                }}
                                variant="outline"
                                style={{ width: '248px' }}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download QR Code
                              </Button>
                            </>
                          )}
                        </div>
                      </div>


                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center space-y-4 sm:space-y-6">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-muted to-accent rounded-2xl flex items-center justify-center shadow-inner mx-auto">
                          <Smartphone className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
                        </div>
                        <div className="space-y-3 sm:space-y-4">
                          <h3 className="text-2xl sm:text-3xl font-bold text-card-foreground">AR Preview</h3>
                          <p className="text-muted-foreground max-w-lg text-sm sm:text-base">Select a 3D model and generate your AR content. The result will appear here.</p>
                          <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground pt-2">
                            <div className="flex items-center space-x-2">
                              <Wand2 className="w-4 h-4 text-emerald-500" />
                              <span>AI-Powered</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <QrCode className="w-4 h-4 text-teal-500" />
                              <span>AR Ready</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Download className="w-4 h-4 text-cyan-500" />
                              <span>Downloadable</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>


        </TooltipProvider>
      </div>
    </div>
    </ComingSoonWrapper>
  );
};

export default AdStudioAR2Page;